import { Repeat } from "lucide-react";

import { formatEuro } from "../gulaConstants";
import {
  EXPENSE_CATEGORIES,
  VAT_MODES,
  labelFor,
  round2,
  type AccExpense,
  type AccSupplier,
} from "./accounting";

/** Suppliers with what has been spent with each in the period. */
export const SuppliersView = ({
  suppliers,
  expenses,
  onEdit,
}: {
  suppliers: AccSupplier[];
  /** Expenses of the selected period */
  expenses: AccExpense[];
  onEdit: (supplier: AccSupplier) => void;
}) => {
  const spentBy = (supplier: AccSupplier) =>
    round2(
      expenses
        .filter(
          (expense) =>
            expense.supplier_id === supplier.id ||
            expense.supplier_name.toLowerCase() === supplier.name.toLowerCase(),
        )
        .reduce(
          (sum, expense) =>
            sum + Number(expense.base_amount) + Number(expense.other_amount),
          0,
        ),
    );
  const rows = suppliers
    .map((supplier) => ({ supplier, spent: spentBy(supplier) }))
    .sort(
      (a, b) =>
        b.spent - a.spent || a.supplier.name.localeCompare(b.supplier.name),
    );

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Proveedor</th>
            <th className="px-3 py-2 text-left font-medium">Categoría</th>
            <th className="px-3 py-2 text-left font-medium">IVA</th>
            <th className="px-3 py-2 text-right font-medium">
              Gastado en el periodo
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ supplier, spent }) => (
            <tr
              key={String(supplier.id)}
              onClick={() => onEdit(supplier)}
              className="cursor-pointer border-t hover:bg-muted/40"
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5 font-medium">
                  {supplier.name}
                  {supplier.recurring ? (
                    <Repeat
                      className="h-3.5 w-3.5 text-muted-foreground"
                      aria-label="Factura todos los meses"
                    />
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {supplier.tax_id || "Sin NIF"}
                </div>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {labelFor(EXPENSE_CATEGORIES, supplier.default_category)}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {labelFor(VAT_MODES, supplier.default_vat_mode)}
                {supplier.default_vat_mode === "exempt"
                  ? ""
                  : ` · ${Number(supplier.default_vat_rate)} %`}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {spent ? formatEuro(spent) : "—"}
              </td>
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td
                colSpan={4}
                className="px-3 py-8 text-center text-muted-foreground"
              >
                Aún no hay proveedores.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};
